import React from "react";
import { Node, NodeProps, Position, useReactFlow, useUpdateNodeInternals } from "reactflow";
import { operationType } from "../../../../../services/types";
import CopyToClipboard from "react-copy-to-clipboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import { faClipboard } from "@fortawesome/free-regular-svg-icons";
import { Main, Styled } from "./style";
import { TextInput } from "../../../../form/textInput";
import { addUpdateNodeToQueue, updateNodeValue } from "../../../../../services/method";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import axios from "axios";
import { webURL } from "../../../../../services/API";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type WebhookProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

const MAX_NUM_EXTRACTORS = 4;

export function Webhook({ id, data, setChildMinimize, setChildSettings }: WebhookProps) {
   const updateNodeInternals = useUpdateNodeInternals();
   const userId = useSelector((state: RootState) => state.user.data?._id);
   const [isCopy, setIsCopy] = React.useState<boolean>(false);
   const [searchParams] = useSearchParams();
   const [apiKey, setApiKey] = React.useState('');
   const { setNodes, setEdges } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value?.extractors) {
         updateNodeValue(id, setNodes, { extractors: [{ id: 1, value: '' }, { id: 2, value: '' }, { id: 3, value: '' }] });
      }
      axios.get(`${webURL}/projects/workflow?id=${searchParams.get('id')}&select=apiKey`).then(res => {
         setApiKey(res.data.apiKey);
      });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   React.useLayoutEffect(() => {
      if (data.value?.extractors?.length) {
         addUpdateNodeToQueue(setNodes, (nodes) => nodes.map((node: Node<operationType>) => {
            if (node.id === id) {
               const postions = [
                  { type: 'source', label: 'Full-Output', dir: Position.Right },
                  ...data.value?.extractors.map((_: any, index: number) => ({ type: 'source', label: `Extractor-${index+1}`, dir: Position.Right }))
               ];
               return { ...node, data: { ...node.data, postions }}
            } else return node;
         }));
         requestAnimationFrame(() => {
            updateNodeInternals(id);
         });
      }
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [data.value?.extractors?.length]);

   // ---
   const handleAddExtractor = (type: 'add' | 'remove', itemId?: number) => {
      if (type === 'add') {
         updateNodeValue(id, setNodes, { extractors: [...data.value.extractors, { id: data.value.extractors.findLastIndex((i: any) => i)+2, value: '' }] });
      } else {
         updateNodeValue(id, setNodes, { extractors: data.value.extractors.filter((item: any) => item.id !== itemId) });
         setEdges(edges => edges.filter(edge => {
            return edge.target !== id && edge.sourceHandle !== `Extractor-${data.value.extractors.findIndex((i: any) => i.id === itemId)+1}`
         }));
      }
   }
   // ---
   const handleInputExtractor = (e: React.ChangeEvent<HTMLInputElement>, itemId: any) => {
      const extractors = [...data.value.extractors];

      for (const item of extractors) {
         if (item.id === itemId) item.value = e.target.value;
      }
      updateNodeValue(id, setNodes, { extractors });
   }

   // ---
   const link = `${webURL}/user/webhook?userId=${userId}&projectId=${searchParams.get('id')}&blockType=${data.action}&blockId=${id}&blockInx=${data.index}&projectApiKey=${apiKey}`;
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={ id } data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={ 21 } /></span>
         </> : null }
         { isExpanded ? <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ width: '420px' }}
         ><Styled>
            <CopyToClipboard 
               text={ link }
               onCopy={ _=> {
                  setIsCopy(true);
                  setTimeout(() => setIsCopy(false), 500);
               }}
            >
               <div className="link" title={ link }>
                  <FontAwesomeIcon icon={ isCopy ? faCheck : faClipboard } />
                  <span>{ link }</span>
               </div>
            </CopyToClipboard>
            <div 
               className={`addExtractor ${data.value?.extractors?.length >= MAX_NUM_EXTRACTORS ? 'disabled' : ''}`} 
               onClick={ _=> handleAddExtractor('add') }
               style={{ background: data.background }}
            >
               <FontAwesomeIcon icon={ faPlus } />
               <span>Add Extractor</span>
            </div>
            { data.value?.extractors?.map((item: any, index: number) => (
               <div key={ index.toString() } className="extractor">
                  <TextInput keyRef={ item.id } placeholder={`Extractor Key ${index+1}`} value={ item.value } handleChange={ handleInputExtractor } />
                  { item.id !== 1 ? <div className="remove" onClick={ _=> handleAddExtractor('remove', item.id) }><FontAwesomeIcon icon={ faTimes } /></div> : null }
               </div>
            ))}
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}